import Typography from "components/elements/Typography";
import I18n from "components/materials/I18n";
import classes from "./classes.module.scss";
import { container } from "tsyringe";
import MenuModal from "components/elements/MenuModal";
import { EllipsisVerticalIcon, ArrowDownTrayIcon, ArrowDownCircleIcon, EyeIcon, PencilIcon, PaperAirplaneIcon, ArchiveBoxArrowDownIcon } from "@heroicons/react/20/solid";
import { useNavigate } from "react-router-dom";
import { useContext } from "react";
import { AuthContext } from "contexts/Auth";
import ProjectService from "services/ProjectService";
import ModuleConfig from "configs/ModuleConfig";
import ProjectResponseResource from "common/resources/Project/ProjectResponseResource";
import ConfigService from "services/projects/ConfigService";

const projectService = container.resolve(ProjectService);
const projectConfigService = container.resolve(ConfigService);
const modules = container.resolve(ModuleConfig).get().modules;

type IProps = {
	project: ProjectResponseResource;
	onRowClick: (project: ProjectResponseResource) => void;
	downloadProof: (id: string, title: string) => void;
	openAnchorModal: () => void;
	onProjectChange: () => void;
};

export default function Options({ project, onRowClick, downloadProof, openAnchorModal, onProjectChange }: IProps) {
	const { jwtContent } = useContext(AuthContext);
	const navigate = useNavigate();
	const hasSubscriptionPlan = Boolean(jwtContent?.subscriptionPlan);

	const navigateToEdit = () => {
		if (!project) return;
		const path = hasSubscriptionPlan
			? modules.pages.EditProjectConfig.props.path.replace(":projectId", project.id)
			: modules.pages.EditProject.props.path.replace(":projectId", project.id);
		navigate(path);
	};

	const createOption = (icon: JSX.Element, label: string, onClick: (e: React.MouseEvent) => void) => (
		<div
			onClick={(e) => {
				e.stopPropagation();
				onClick(e);
			}}
			className={classes["option-item"]}>
			{icon}
			<Typography type="span" size="medium" weight="regular">
				{label}
			</Typography>
		</div>
	);

	const options = [
		!project.scenario?.anchor?.id && createOption(<PencilIcon className={classes["icons"]} />, I18n.asset.pages.my_projects.table.menu.edit, navigateToEdit),
		createOption(<EyeIcon className={classes["icons"]} />, I18n.asset.pages.my_projects.table.menu.data, () => onRowClick(project)),
		project.scenario?.anchor?.txLink &&
			createOption(<ArrowDownTrayIcon className={classes["icons"]} />, I18n.asset.pages.my_projects.table.menu.download_proof, () =>
				downloadProof(project.id, project.title),
			),
		!project.scenario?.anchor?.id && createOption(<ArrowDownCircleIcon className={classes["icons"]} />, I18n.asset.pages.my_projects.table.menu.anchor, openAnchorModal),
		hasSubscriptionPlan &&
			!project.publishedAt &&
			createOption(<PaperAirplaneIcon className={classes["icons"]} />, I18n.asset.pages.my_projects.table.menu.publish, () => projectConfigService.publish(project.id).then(onProjectChange)),
		hasSubscriptionPlan &&
			!project.archivedAt &&
			createOption(<ArchiveBoxArrowDownIcon className={classes["icons"]} />, I18n.asset.pages.my_projects.table.menu.archive, () =>
				projectService.archive(project.id).then(onProjectChange),
			),
		hasSubscriptionPlan &&
			project.archivedAt &&
			createOption(<ArchiveBoxArrowDownIcon className={classes["icons"]} />, I18n.asset.pages.my_projects.table.menu.restore, () =>
				projectService.restore(project.id).then(onProjectChange),
			),
	].filter(Boolean);

	return (
		<MenuModal options={options}>
			<EllipsisVerticalIcon width={24} height={24} />
		</MenuModal>
	);
}
